import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router';

import { EnvironmentVariableEnum } from '../../constants/environment-variable.ts';
import { EnvironmentEnum } from '../../constants/environment.ts';

import { getAppBuildUtil } from '../app-build.util.ts';
import { getUrlUtil } from '../url.util.ts';

export class SentryTrackerUtil {
  private isInitialized = false;

  private static instance: SentryTrackerUtil;

  static getInstance(): SentryTrackerUtil {
    if (!SentryTrackerUtil.instance) {
      SentryTrackerUtil.instance = new SentryTrackerUtil(EnvironmentVariableEnum.SENTRY_DSN);
    }

    return SentryTrackerUtil.instance;
  }

  constructor(
    private readonly dsn: string,
    private readonly appBuildUtil = getAppBuildUtil(),
    private readonly urlUtil = getUrlUtil()
  ) {
    this.initClient();
  }

  initClient(): void {
    if (!this.dsn) {
      return;
    }

    const isProductionBuildAndProductionUrl = this.appBuildUtil.isProductionBuild() && this.urlUtil.isProductionUrl();

    let environment = EnvironmentEnum.INTEGRATION;
    if (isProductionBuildAndProductionUrl) {
      environment = EnvironmentEnum.PRODUCTION;
    } else if (this.urlUtil.isLocalhostUrl()) {
      environment = EnvironmentEnum.LOCALHOST;
    }

    Sentry.init({
      dsn: this.dsn,
      tracesSampleRate: isProductionBuildAndProductionUrl ? 0.8 : 0.5,
      environment,
      enabled: environment !== EnvironmentEnum.LOCALHOST,
      debug: environment === EnvironmentEnum.LOCALHOST,
      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
    });

    this.isInitialized = true;
  }

  trackError(error: Error, tags: Record<string, string | number | undefined>, extra?: Record<string, unknown>): void {
    if (!this.isInitialized) {
      console.error('[ERROR]', error);
      return;
    }

    Sentry.captureException(error, {
      tags,
      extra,
      level: 'error',
    });
  }

  setUser(id: string, email: string): void {
    if (!this.isInitialized) {
      return;
    }

    Sentry.setUser({ id, email });
    Sentry.setExtra('userId', id);
    Sentry.setExtra('userEmail', email);
  }

  resetUser(): void {
    if (!this.isInitialized) {
      return;
    }

    Sentry.setUser(null);
    Sentry.setExtra('userId', null);
    Sentry.setExtra('email', null);
  }
}

export const getSentryUtil = (): SentryTrackerUtil => SentryTrackerUtil.getInstance();
